import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page TerminologyPage
 * @description 術語對照表管理頁（UI-8-3）
 * 最高管理員可自訂場館用語，即時全站生效
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { api, apiClient } from '../../lib/api';

type TermRow = {
  id: string;
  namespace: string;
  termKey: string;
  zhTW: string;
  zhCN?: string | null;
  en?: string | null;
  updatedAt: string;
};

const NAMESPACES = ['common', 'member', 'accounting', 'tournament', 'auth', 'display'];

export default function TerminologyPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();

  const [filterNs, setFilterNs] = useState<string>('');
  const [editRow, setEditRow] = useState<TermRow | null>(null);
  const dialogRef = useDialogFocus(!!editRow);
  const [formData, setFormData] = useState({ zhTW: '', zhCN: '', en: '' });

  const { data: terms = [], isLoading } = useQuery<TermRow[]>({
    queryKey: ['i18n-terms', filterNs],
    queryFn: () =>
      api.get<TermRow[]>(`/i18n/custom-terms${filterNs ? `?namespace=${filterNs}` : ''}`),
  });

  const upsertMutation = useMutation({
    mutationFn: ({ namespace, key, dto }: { namespace: string; key: string; dto: object }) =>
      apiClient.put(`/i18n/custom-terms/${namespace}/${key}`, dto).then((r) => r.data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['i18n-terms'] });
      // Update i18n bundle only after server confirms
      const dto = variables.dto as { zhTW?: string };
      if (dto.zhTW) {
        i18n.addResourceBundle(
          'zh-TW',
          variables.namespace,
          { [variables.key]: dto.zhTW },
          true,
          true,
        );
      }
      setEditRow(null);
    },
    onError: (err) => {
      console.warn('[Terminology] upsert failed:', (err as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ namespace, key }: { namespace: string; key: string }) =>
      api.delete(`/i18n/custom-terms/${namespace}/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['i18n-terms'] }),
    onError: (err) => {
      console.warn('[Terminology] delete failed:', (err as Error).message);
    },
  });

  const handleEdit = (row: TermRow) => {
    setEditRow(row);
    setFormData({ zhTW: row.zhTW, zhCN: row.zhCN ?? '', en: row.en ?? '' });
  };

  const handleSave = () => {
    if (!editRow) return;
    upsertMutation.mutate({
      namespace: editRow.namespace,
      key: editRow.termKey,
      dto: { zhTW: formData.zhTW, zhCN: formData.zhCN || undefined, en: formData.en || undefined },
    });
  };

  return (
    <AdminPage className="admin-page-body">
      <AdminPageHeader title={t('terminologyTitle')} description={t('terminologySubtitle')} />

      {/* Namespace 篩選 */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setFilterNs('')}
          className={`px-3 py-1 rounded-full text-sm border transition-colors
            ${filterNs === '' ? 'bg-primary text-primary-content border-primary' : 'border-base-300 text-base-content hover:bg-primary/10'}`}
        >
          {t('terminologyAll')}
        </button>
        {NAMESPACES.map((ns) => (
          <button
            key={ns}
            onClick={() => setFilterNs(ns)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors
              ${filterNs === ns ? 'bg-primary text-primary-content border-primary' : 'border-base-300 text-base-content hover:bg-primary/10'}`}
          >
            {ns}
          </button>
        ))}
      </div>

      <p className="sm:hidden text-xs text-base-content/60 mb-2">
        {t('terminologyScrollHint', { defaultValue: '左右滑動查看所有語言與操作' })}
      </p>
      {/* 術語表格 */}
      <div
        className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto"
        tabIndex={0}
        aria-label={t('terminologyTitle')}
      >
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-base-300">
            <tr>
              <th className="text-left px-4 py-3 text-base-content font-medium">Namespace</th>
              <th className="text-left px-4 py-3 text-base-content font-medium">Key</th>
              <th className="text-left px-4 py-3 text-base-content font-medium">
                {t('terminologyZhTW')}
              </th>
              <th className="text-left px-4 py-3 text-base-content font-medium">
                {t('terminologyZhCN')}
              </th>
              <th className="text-left px-4 py-3 text-base-content font-medium">English</th>
              <th className="text-left px-4 py-3 text-base-content font-medium">
                {t('terminologyActions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-base-content/40">
                  {t('loading', { ns: 'common' })}
                </td>
              </tr>
            ) : terms.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-base-content/40">
                  {t('noData', { ns: 'common' })}
                </td>
              </tr>
            ) : (
              terms.map((row) => (
                <tr key={row.id} className="hover:bg-primary/5 transition-colors">
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-mono">
                      {row.namespace}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-base-content/70">
                    {row.termKey}
                  </td>
                  <td className="px-4 py-3 text-base-content">{row.zhTW}</td>
                  <td className="px-4 py-3 text-base-content/70">{row.zhCN ?? '-'}</td>
                  <td className="px-4 py-3 text-base-content/70">{row.en ?? '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(row)}
                        className="min-h-11 min-w-11 text-primary hover:underline text-sm"
                      >
                        {t('edit', { ns: 'common' })}
                      </button>
                      <button
                        onClick={() =>
                          deleteMutation.mutate({ namespace: row.namespace, key: row.termKey })
                        }
                        className="min-h-11 min-w-11 text-error hover:underline text-sm"
                      >
                        {t('delete', { ns: 'common' })}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 編輯 Modal */}
      {editRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('terminologyEditTitle')}
            tabIndex={-1}
            className="bg-base-200 rounded-xl shadow-2xl w-full max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto p-4 sm:p-6"
          >
            <h2 className="text-lg font-semibold mb-4">
              {t('terminologyEditTitle')}
              <span className="font-mono text-sm text-primary">
                {editRow.namespace}:{editRow.termKey}
              </span>
            </h2>

            <div className="space-y-3">
              {[
                { label: t('terminologyZhTWRequired'), key: 'zhTW', required: true },
                { label: t('terminologyZhCN'), key: 'zhCN', required: false },
                { label: 'English', key: 'en', required: false },
              ].map(({ label, key, required }) => (
                <label key={key} className="block">
                  <span className="text-sm text-base-content/70 mb-1 block">{label}</span>
                  <input
                    type="text"
                    value={formData[key as keyof typeof formData]}
                    onChange={(e) => setFormData((p) => ({ ...p, [key]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-200
                               text-base-content text-base min-h-11 focus:outline-hidden focus:ring-2 focus:ring-primary"
                    required={required}
                  />
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditRow(null)}
                className="min-h-11 px-4 py-2 rounded-md border border-base-300 text-sm text-base-content hover:bg-base-300"
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.zhTW || upsertMutation.isPending}
                className="min-h-11 px-4 py-2 rounded-md bg-primary text-primary-content text-sm
                           hover:bg-primary/90 disabled:opacity-50"
              >
                {upsertMutation.isPending
                  ? t('loading', { ns: 'common' })
                  : t('save', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
